import React, { useEffect } from "react";
import DashboardWrapper from "../../layout/DashboardWrapper/DashboardWrapper";
import ProfileForm from "../../components/ProfileForm/ProfileForm";
import Grid from "@mui/material/Grid";
import styles from "../../styles/components/profileform.module.scss";
import { Box, MenuItem, Select, Stack, Typography } from "@mui/material";
import CustomButton from "../../ui/CustomButton/CustomButton";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import CustomInput from "../../components/CustomInput/CustomInput";
import { useMutation, useQuery, useQueryClient } from "react-query";
import stylesAdminPart from "../../styles/pages/admin.module.scss";
import TableSection from "../../components/TableSection/TableSection";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import validationText from "../../json/messages/validationText";
import { AdminUserList, UpdateMCAdminUser } from "../../api/functions/user.api";
import { useNavigate } from "react-router-dom";
import useNotiStack from "../../hooks/useNotistack";
import BackdropLoader from "../../ui/BackdropLoader/BackdropLoader";
import {
  adminMemberData,
  adminUserId,
  isPasswordMismatch,
  toUppercaseWithConcat,
} from "../../services/utils";
import { useAdminUserDetails } from "../../hooks/useSelector";
import { administratorList } from "../../json/commonData/jsonListData";

const schema = yup.object().shape(
  {
    firstName: yup
      .string()
      .trim()
      .required(
        <p style={{ fontSize: "12px" }}>{validationText.error.first_name}</p>
      ),
    lastName: yup.string().trim().required().label("Last name"),
    email: yup
      .string()
      .trim()
      .required(
        <p style={{ fontSize: "12px" }}>{validationText.error.enter_email}</p>
      )
      .matches(
        /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
        "Email address is not valid"
      ),
    password: yup
      .string()
      //  .trim()
      .when("password", {
        is: (value) => value,
        then: (rule) => rule.min(4).max(15).label("password"),
      })
      .when("confirmPassword", {
        is: (value) => value,
        then: (rule) => rule.min(4).max(15).label("password"),
      }),
    confirmPassword: yup.string().when("password", {
      is: (value) => value,
      then: (rule) =>
        rule
          .required()
          // .oneOf([yup.ref("password"), null])
          .label("Confirm password"),
    }),
  },
  [
    ["password", "password"],
    ["confirmPassword", "password"],
  ]
);

export default function UserProfile() {
  const userId = adminUserId();
  const user = adminMemberData();
  const profileDetailsQuery = useAdminUserDetails();
  const { toastSuccess, toastError } = useNotiStack();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    handleSubmit,
    control,
    reset,
    getValues,
    watch,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(schema),
  });
  const isNotMatchPassword = isPasswordMismatch(
    watch("password"),
    watch("confirmPassword")
  );

  const adminAccountListing = useQuery(["adminAccountList"], () =>
    AdminUserList({}, user)
  );
  const {
    mutateAsync: mutateAsyncUpdateMCAdminUser,
    isLoading: updatingProfileStatus,
  } = useMutation({
    mutationFn: (payloadData) => UpdateMCAdminUser(payloadData, user),
    onSuccess: (response) => {
      if (response.Status === 1) {
        queryClient.invalidateQueries(["userdetails"]);
        toastSuccess("Profile updated successfully");
        reset();
      }
    },
    onError: (response) => {
      toastError("Something went wrong.");
    },
  });

  const onSubmit = (data) => {
    const formData = new FormData();
    formData.append("firstName", data?.firstName?.trim());
    formData.append("lastName", data?.lastName?.trim());
    formData.append("email", data?.email?.trim());
    formData.append("administrator", data?.adminLevel);
    if (data.password?.length > 0) {
      formData.append("password", data?.password?.trim());
    }
    formData.append("id_adminuser", Number(userId));

    mutateAsyncUpdateMCAdminUser(formData);
  };

  const closeBtn = () => {
    navigate("/dashboard/search-results");
  };
  const redirectToAddUser = () => {
    navigate("/dashboard/create-admin-user");
  };
  const redirectToAdminAccountDetails = (e, id) => {
    navigate(`/dashboard/user-profile-edit-user/${id}`);
  };

  useEffect(() => {
    reset({
      firstName: profileDetailsQuery?.firstName,
      lastName: profileDetailsQuery?.lastName,
      email: profileDetailsQuery?.email,
      adminLevel: profileDetailsQuery?.administrator,
    });
  }, [profileDetailsQuery]);

  return (
    <DashboardWrapper showBtmHeader={false} isList={false}>
      <BackdropLoader open={adminAccountListing?.isLoading} />
      {profileDetailsQuery?.administrator === 1 && (
        <Box className={stylesAdminPart.result_btn}>
          <CustomButton onClick={redirectToAddUser}>
            <Typography variant="caption">Add User</Typography>
          </CustomButton>
        </Box>
      )}
      <ProfileForm formHeader="My Profile">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container rowSpacing={{ md: 4, xs: 2.5 }} columnSpacing={2}>
            <Grid item xs={12} lg={5} md={6} sm={6}>
              <Box className={styles.profile_grid}>
                <Stack
                  flexDirection="row"
                  className={styles.profile_stack}
                  alignItems="center"
                >
                  <label>First Name</label>
                  <Box className={styles.form_input_group}>
                    <Controller
                      control={control}
                      name="firstName"
                      render={({ field: { onChange, value } }) => (
                        <CustomInput
                          type="text"
                          value={value ? value : ""}
                          onChange={onChange}
                          disabled={updatingProfileStatus}
                          error={Boolean(errors?.firstName)}
                          helperText={errors?.firstName?.message}
                        />
                      )}
                    />
                  </Box>
                </Stack>
              </Box>
              <Box className={styles.profile_grid}>
                <Stack
                  flexDirection="row"
                  className={styles.profile_stack}
                  alignItems="center"
                >
                  <label>Last Name</label>
                  <Box className={styles.form_input_group}>
                    <Controller
                      control={control}
                      name="lastName"
                      render={({ field: { onChange, value } }) => (
                        <CustomInput
                          type="text"
                          value={value ? value : ""}
                          onChange={onChange}
                          disabled={updatingProfileStatus}
                          error={Boolean(errors?.lastName)}
                          helperText={errors?.lastName?.message}
                        />
                      )}
                    />
                  </Box>
                </Stack>
              </Box>
              <Box className={`${styles.profile_grid} ${styles.mb_0}`}>
                <Stack
                  flexDirection="row"
                  className={styles.profile_stack}
                  alignItems="center"
                >
                  <label>Email</label>
                  <Box className={styles.form_input_group}>
                    <Controller
                      control={control}
                      name="email"
                      render={({ field: { onChange, value } }) => (
                        <CustomInput
                          type="text"
                          value={value ? value : ""}
                          onChange={onChange}
                          disabled={updatingProfileStatus}
                          error={Boolean(errors?.email)}
                          helperText={errors?.email?.message}
                        />
                      )}
                    />
                  </Box>
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} lg={5} md={6} sm={6}>
              {profileDetailsQuery?.administrator === 1 && (
                <Box className={styles.profile_grid}>
                  <Stack
                    flexDirection="row"
                    className={styles.profile_stack}
                    alignItems="center"
                  >
                    <label>Admin User Level</label>
                    <Box className={styles.form_input_group}>
                      <Controller
                        control={control}
                        name="adminLevel"
                        render={({ field: { onChange, value } }) => (
                          <Box className={styles.selectWrap}>
                            <Select
                              className={styles.cmn_slct}
                              defaultValue=""
                              id="demo-simple-select"
                              onChange={onChange}
                              // value={value}
                              value={value ? value : ""}
                              disabled={updatingProfileStatus}
                            >
                              {administratorList?.map((e, index) => (
                                <MenuItem key={e?._id} value={e?._id}>
                                  <Typography
                                    variant="body1"
                                    className="menu_para"
                                  >
                                    {e?.name}
                                  </Typography>
                                </MenuItem>
                              ))}
                            </Select>
                          </Box>
                        )}
                      />
                    </Box>
                  </Stack>
                </Box>
              )}

              <Box className={styles.profile_grid}>
                <Stack
                  flexDirection="row"
                  className={styles.profile_stack}
                  alignItems="center"
                >
                  <label>Password</label>
                  <Box className={styles.form_input_group}>
                    <Controller
                      control={control}
                      name="password"
                      render={({ field: { onChange, value } }) => (
                        <CustomInput
                          type="password"
                          value={value ? value : ""}
                          onChange={onChange}
                          disabled={updatingProfileStatus}
                          error={Boolean(errors?.password)}
                          helperText="enter at least 4 characters"
                        />
                      )}
                    />
                  </Box>
                </Stack>
              </Box>
              <Box className={`${styles.profile_grid} ${styles.mb_0}`}>
                <Stack
                  flexDirection="row"
                  className={styles.profile_stack}
                  alignItems="center"
                >
                  <label>Confirm Password</label>
                  <Box className={styles.form_input_group}>
                    <Controller
                      control={control}
                      name="confirmPassword"
                      render={({ field: { onChange, value } }) => (
                        <CustomInput
                          type="password"
                          value={value ? value : ""}
                          onChange={onChange}
                          disabled={updatingProfileStatus}
                          error={Boolean(errors?.confirmPassword)}
                          helperText="required!"
                        />
                      )}
                    />
                    {getValues("confirmPassword") && isNotMatchPassword && (
                      <p className={styles.error_text}>
                        {validationText.error.password_mismatch}
                      </p>
                    )}
                  </Box>
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} lg={2} md={12} sm={12}>
              <Box className={styles.profile_btn}>
                <Box className={styles.black_btn}>
                  <CustomButton onClick={closeBtn}>
                    <Typography variant="caption">Close</Typography>
                  </CustomButton>
                </Box>

                <CustomButton
                  type="submit"
                  disabled={isDirty === false || isNotMatchPassword}
                  loading={updatingProfileStatus}
                >
                  {" "}
                  <Typography variant="caption">
                    {" "}
                    {updatingProfileStatus ? "Saving.." : "Save Profile"}
                  </Typography>
                </CustomButton>
              </Box>
            </Grid>
          </Grid>
        </form>
      </ProfileForm>
      {profileDetailsQuery?.administrator === 1 && (
        <>
          <Box className={stylesAdminPart.tabletitle}>
            <Typography variant="h4">Admin Accounts</Typography>
          </Box>
          <TableSection>
            <TableHead>
              <TableRow>
                <TableCell align="center">
                  <Typography variant="body1">Name</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body1">Email</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body1">Admin Level</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            {adminAccountListing?.data?.Status === 1 ? (
              adminAccountListing?.data?.adminArray?.map((row) => (
                <TableBody>
                  <TableRow
                    key={row.id_adminuser}
                    className={styles.selectedAccount}
                    onClick={(event) =>
                      redirectToAdminAccountDetails(event, row.id_adminuser)
                    }
                  >
                    <TableCell align="center" scope="row">
                      <Typography variant="body1">
                        {toUppercaseWithConcat(row.firstName, row.lastName)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body1">{row.email}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body1">
                        {row.administrator === 1
                          ? "Super Admin"
                          : "General Admin"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              ))
            ) : (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={21}>
                    <Typography
                      variant="body1"
                      className={stylesAdminPart.noData}
                    >
                      No accounts found
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </TableSection>
        </>
      )}

      {/* } */}
    </DashboardWrapper>
  );
}
